/**
 * Upload-then-reference flow shared by Creations "Upload media" and Brand Assets "Import":
 *   1. `uploadFile` (multipart POST /api/uploads) → {key, url, …}
 *   2. call the tool with `storageKey: key`.
 * Today the server stores uploads under `uploads/<brandId>/…` while creatives.upload / assets.upload only
 * accept keys under `brands/<brandId>/…`; on that specific refusal we retry once with the absolute `url`
 * (the tool then fetches + rehosts it). Remove the fallback once the prefixes agree.
 */
import { ApiError } from "@/api/client";

export function isForeignStorageKeyError(e: unknown): boolean {
  return e instanceof ApiError && /storageKey must belong/i.test(e.message);
}

export function absoluteUrl(url: string): string {
  try {
    return new URL(url, window.location.origin).href;
  } catch {
    return url;
  }
}

/** Run `call(ref)` with `{storageKey}`; on a foreign-key refusal retry with `{url}`. */
export async function withStorageRef<T>(media: { key: string; url: string }, call: (ref: { storageKey: string } | { url: string }) => Promise<T>): Promise<T> {
  try {
    return await call({ storageKey: media.key });
  } catch (e) {
    if (!isForeignStorageKeyError(e) || !media.url) throw e;
    return call({ url: absoluteUrl(media.url) });
  }
}
